import { supabase } from '../lib/supabase';

// Convert snake_case to camelCase
const toEndoReportObject = (dbReport) => ({
  id: dbReport?.id,
  practitionerId: dbReport?.practitioner_id,
  authorId: dbReport?.author_id,
  referralId: dbReport?.referral_id,
  patientFirstName: dbReport?.patient_first_name,
  patientLastName: dbReport?.patient_last_name,
  patientYearOfBirth: dbReport?.patient_year_of_birth,
  patientIdReference: dbReport?.patient_id_reference,
  referrerName: dbReport?.referrer_name,
  referrerSpecialty: dbReport?.referrer_specialty,
  referrerPractice: dbReport?.referrer_practice,
  referrerEmail: dbReport?.referrer_email,
  referrerPhone: dbReport?.referrer_phone,
  reportType: dbReport?.report_type,
  status: dbReport?.status,
  title: dbReport?.title,
  content: dbReport?.content,
  sourceNotes: dbReport?.source_notes,
  toothNumbers: dbReport?.tooth_numbers || [],
  diagnosis: dbReport?.diagnosis,
  proceduresPerformed: dbReport?.procedures_performed || [],
  attachments: dbReport?.attachments || [],
  version: dbReport?.version,
  parentVersionId: dbReport?.parent_version_id,
  createdAt: dbReport?.created_at,
  updatedAt: dbReport?.updated_at,
  sentAt: dbReport?.sent_at,
  pdfUrl: dbReport?.pdf_url,
  pdfPath: dbReport?.pdf_path
});

// Convert camelCase to snake_case
const toDbObject = (report) => {
  const dbObject = {
    practitioner_id: report?.practitionerId,
    author_id: report?.authorId,
    referral_id: report?.referralId,
    patient_first_name: report?.patientFirstName,
    patient_last_name: report?.patientLastName,
    patient_year_of_birth: report?.patientYearOfBirth,
    patient_id_reference: report?.patientIdReference,
    referrer_name: report?.referrerName,
    referrer_specialty: report?.referrerSpecialty,
    referrer_practice: report?.referrerPractice,
    referrer_email: report?.referrerEmail,
    referrer_phone: report?.referrerPhone,
    report_type: report?.reportType,
    status: report?.status,
    title: report?.title,
    content: report?.content,
    source_notes: report?.sourceNotes,
    tooth_numbers: report?.toothNumbers,
    diagnosis: report?.diagnosis,
    procedures_performed: report?.proceduresPerformed,
    attachments: report?.attachments,
    pdf_url: report?.pdfUrl,
    pdf_path: report?.pdfPath
  };

  // Remove undefined values
  Object.keys(dbObject)?.forEach(key => {
    if (dbObject?.[key] === undefined) {
      delete dbObject?.[key];
    }
  });

  return dbObject;
};

export const endoReportService = {
  /**
   * Get all reports for a practitioner
   */
  async getAll(practitionerId, filters = {}) {
    try {
      let query = supabase?.from('endo_reports')?.select('*');

      if (practitionerId) {
        query = query?.eq('practitioner_id', practitionerId);
      }

      query = query?.order('created_at', { ascending: false });

      // Apply filters
      if (filters?.status && filters?.status !== 'all') {
        query = query?.eq('status', filters?.status);
      }

      if (filters?.reportType && filters?.reportType !== 'all') {
        query = query?.eq('report_type', filters?.reportType);
      }

      if (filters?.searchTerm) {
        query = query?.or(
          `patient_first_name.ilike.%${filters?.searchTerm}%,` +
          `patient_last_name.ilike.%${filters?.searchTerm}%,` +
          `referrer_name.ilike.%${filters?.searchTerm}%`
        );
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(toEndoReportObject) || [];
    } catch (error) {
      console.error('Error fetching endo reports:', error);
      throw error;
    }
  },

  /**
   * Get report by ID
   */
  async getById(id) {
    try {
      const { data, error } = await supabase?.from('endo_reports')?.select('*')?.eq('id', id)?.single();

      if (error) throw error;

      return toEndoReportObject(data);
    } catch (error) {
      console.error('Error fetching endo report:', error);
      throw error;
    }
  },

  /**
   * Create new report
   */
  async create(reportData) {
    try {
      const dbData = toDbObject(reportData);
      dbData.status = 'draft'; // Always start as draft

      const { data, error } = await supabase?.from('endo_reports')?.insert(dbData)?.select()?.single();

      if (error) throw error;

      return toEndoReportObject(data);
    } catch (error) {
      console.error('Error creating endo report:', error);
      throw error;
    }
  },

  /**
   * Update report
   */
  async update(id, reportData) {
    try {
      const dbData = toDbObject(reportData);

      const { data, error } = await supabase?.from('endo_reports')?.update(dbData)?.eq('id', id)?.select()?.single();

      if (error) throw error;

      return toEndoReportObject(data);
    } catch (error) {
      console.error('Error updating endo report:', error);
      throw error;
    }
  },

  /**
   * Delete report
   */
  async delete(id) {
    try {
      const { error } = await supabase?.from('endo_reports')?.delete()?.eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting endo report:', error);
      throw error;
    }
  },

  /**
   * Get version history for a report
   */
  async getVersionHistory(reportId) {
    try {
      const { data, error } = await supabase?.from('endo_report_versions')?.select('*')?.eq('report_id', reportId)?.order('version_number', { ascending: false });

      if (error) throw error;

      return data?.map(version => ({
        id: version?.id,
        reportId: version?.report_id,
        versionNumber: version?.version_number,
        content: version?.content,
        changedBy: version?.changed_by,
        changeDescription: version?.change_description,
        createdAt: version?.created_at
      })) || [];
    } catch (error) {
      console.error('Error fetching version history:', error);
      throw error;
    }
  },

  /**
   * Generate AI report from source notes
   */
  async generateAIReport(reportData) {
    try {
      // Get current session for JWT token
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.access_token) {
        throw new Error('Session non authentifiée.');
      }

      console.log('🤖 Calling generate-endo-report Edge Function...');

      const { data, error } = await supabase?.functions?.invoke('generate-endo-report', {
        body: {
          report_type: reportData?.reportType,
          patient_info: {
            first_name: reportData?.patientFirstName,
            last_name: reportData?.patientLastName,
            year_of_birth: reportData?.patientYearOfBirth
          },
          referrer_info: reportData?.referrerName ? {
            name: reportData?.referrerName,
            specialty: reportData?.referrerSpecialty,
            practice: reportData?.referrerPractice
          } : null,
          source_notes: reportData?.sourceNotes,
          tooth_numbers: reportData?.toothNumbers || [],
          diagnosis: reportData?.diagnosis,
          procedures: reportData?.proceduresPerformed || []
        },
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        },
      });

      if (error) {
        console.error('❌ Edge Function error:', error);
        throw new Error(error.message || 'Erreur lors de la génération du compte rendu.');
      }

      if (!data?.content) {
        throw new Error('Aucun contenu retourné par l\'IA.');
      }

      console.log('✅ Report generated successfully');
      return data?.content;
    } catch (error) {
      console.error('Error in generateAIReport:', error);
      throw new Error(error?.message || 'Erreur lors de la génération du compte rendu.');
    }
  },

  /**
   * Refine report content with custom instruction
   */
  async refineContent(reportId, currentContent, instruction) {
    try {
      // Get current session for JWT token
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.access_token) {
        throw new Error('Session non authentifiée.');
      }

      console.log('🤖 Calling refine-endo-report Edge Function...');

      const { data, error } = await supabase?.functions?.invoke('refine-endo-report', {
        body: {
          report_id: reportId,
          content: currentContent,
          instruction: instruction
        },
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        },
      });

      if (error) {
        console.error('❌ Edge Function error:', error);
        throw new Error(error.message || 'Erreur lors du raffinement du compte rendu.');
      }

      if (!data?.content) {
        throw new Error('Aucun contenu retourné par l\'IA.');
      }

      console.log('✅ Content refined successfully');

      return data?.content;
    } catch (error) {
      console.error('Error refining report content:', error);
      throw error;
    }
  },

  /**
   * Generate content for an existing report
   */
  async generateContent(reportId, sourceNotes) {
    try {
      // First, fetch the report to get all necessary data
      const { data: report, error: fetchError } = await supabase
        ?.from('endo_reports')
        ?.select('*')
        ?.eq('id', reportId)
        ?.single();

      if (fetchError || !report) {
        throw new Error('Impossible de récupérer les données du compte rendu.');
      }

      // Get current session for JWT token
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.access_token) {
        throw new Error('Session non authentifiée.');
      }

      console.log('🤖 Calling generate-endo-report Edge Function...');

      const { data, error } = await supabase?.functions?.invoke('generate-endo-report', {
        body: {
          report_type: report?.report_type,
          patient_info: {
            first_name: report?.patient_first_name,
            last_name: report?.patient_last_name,
            year_of_birth: report?.patient_year_of_birth
          },
          referrer_info: report?.referrer_name ? {
            name: report?.referrer_name,
            specialty: report?.referrer_specialty,
            practice: report?.referrer_practice
          } : null,
          source_notes: sourceNotes || report?.source_notes,
          tooth_numbers: report?.tooth_numbers || [],
          diagnosis: report?.diagnosis,
          procedures: report?.procedures_performed || []
        },
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        },
      });

      if (error) {
        console.error('❌ Edge Function error:', error);
        throw new Error(error.message || 'Erreur lors de la génération du compte rendu.');
      }

      if (!data?.content) {
        throw new Error('Aucun contenu retourné par l\'IA.');
      }

      console.log('✅ Report content generated successfully');
      return data?.content;
    } catch (error) {
      console.error('Error in generateContent:', error);
      throw new Error(error?.message || 'Erreur lors de la génération du compte rendu.');
    }
  }
};

export default endoReportService;